/* eslint-disable no-restricted-syntax */
import { Currency, Token } from '@pancakeswap/sdk'
import { Box, Input, Text, useMatchBreakpoints, AutoColumn, Column, AutoRow, CloseIcon, SearchIcon, Flex } from '@pancakeswap/uikit'
import { KeyboardEvent, RefObject, useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { useStableSwapPairs } from 'state/swap/useStableSwapPairs'
import { useTranslation } from '@pancakeswap/localization'
import { useDebounce, useSortedTokensByQuery } from '@pancakeswap/hooks'
import useNativeCurrency from 'hooks/useNativeCurrency'
import { FixedSizeList } from 'react-window'
import { useAllLists, useInactiveListUrls } from 'state/lists/hooks'
import { WrappedTokenInfo, createFilterToken } from '@pancakeswap/token-lists'
import { useAudioPlay } from '@pancakeswap/utils/user'
import { safeGetAddress } from 'utils'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { isAddress } from 'viem'
import ESearchBox from 'components/ESearchBox'
import styled from 'styled-components'
import { useAllTokens, useIsUserAddedToken, useToken } from '../../hooks/Tokens'
import Row from '../Layout/Row'
import CommonBases from './CommonBases'
import CurrencyList from './CurrencyList'
import useTokenComparator from './sorting'
import { getSwapSound } from './swapSound'

import ImportRow from './ImportRow'

const SearchBox = styled(Flex)`
	flex: 1;
	border: 1px solid #FFFFFF33;
	border-radius: 12px;
	padding: 8px 17px;
	align-items: center;
	svg {
		opacity: 0.3;
	}
	& > input {
		background: transparent;
		border: none;
		outline: 0;
		flex: 1;
		font-size: 14px;
		&::placeholder {
			color: #958BB0;
		}
	}
`

interface CurrencySearchProps {
  selectedCurrency?: Currency | null
  onCurrencySelect: (currency: Currency) => void
  otherSelectedCurrency?: Currency | null
  showSearchInput?: boolean
  showCommonBases?: boolean
  commonBasesType?: string
  showImportView: () => void
  setImportToken: (token: Token) => void
  height?: number
  tokensToShow?: Token[]
  mode?: string
  onRampFlow?: boolean
  showOnlyStableCoins?: boolean
}

function useSearchInactiveTokenLists(search: string | undefined, minResults = 10): WrappedTokenInfo[] {
  const lists = useAllLists()
  const inactiveUrls = useInactiveListUrls()
  const { chainId } = useActiveChainId()
  const activeTokens = useAllTokens()
  return useMemo(() => {
    if (!search || search.trim().length === 0) return []
    const filterToken = createFilterToken(search, (address) => isAddress(address))
    const exactMatches: WrappedTokenInfo[] = []
    const rest: WrappedTokenInfo[] = []
    const addressSet: { [address: string]: true } = {}
    const trimmedSearchQuery = search.toLowerCase().trim()
    for (const url of inactiveUrls) {
      const list = lists[url]?.current
      // eslint-disable-next-line no-continue
      if (!list) continue
      for (const tokenInfo of list.tokens) {
        if (
          tokenInfo.chainId === chainId &&
          !(tokenInfo.address in activeTokens) &&
          !addressSet[tokenInfo.address] &&
          filterToken(tokenInfo)
        ) {
          const wrapped: WrappedTokenInfo = new WrappedTokenInfo({
            ...tokenInfo,
            address: safeGetAddress(tokenInfo.address) || tokenInfo.address,
          })
          addressSet[wrapped.address] = true
          if (
            tokenInfo.name?.toLowerCase() === trimmedSearchQuery ||
            tokenInfo.symbol?.toLowerCase() === trimmedSearchQuery
          ) {
            exactMatches.push(wrapped)
          } else {
            rest.push(wrapped)
          }
        }
      }
    }
    return [...exactMatches, ...rest].slice(0, minResults)
  }, [activeTokens, chainId, inactiveUrls, lists, minResults, search])
}

function CurrencySearch({
  selectedCurrency,
  onCurrencySelect,
  otherSelectedCurrency,
  showCommonBases,
  commonBasesType,
  showSearchInput = true,
  showImportView,
  setImportToken,
  height,
  tokensToShow,
  mode,
  onRampFlow,
  showOnlyStableCoins
}: CurrencySearchProps) {
  const { t } = useTranslation()
  const { chainId } = useActiveChainId()

  const inputRef = useRef(null);

  useEffect(() => {
    // Focus the input element when the component mounts
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [])

  // refs for fixed size lists
  const fixedList = useRef<FixedSizeList>()

  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedQuery = useDebounce(searchQuery, 200)

  const [invertSearchOrder] = useState<boolean>(false)

  const allTokens = useAllTokens()
  // if they input an address, use it
  const searchToken = useToken(debouncedQuery)
  const searchTokenIsAdded = useIsUserAddedToken(searchToken)

  const { isMobile } = useMatchBreakpoints()
  const [audioPlay] = useAudioPlay()

  const native = useNativeCurrency()

  const showNative: boolean = useMemo(() => {
    if (tokensToShow || mode === 'onramp-input' || showOnlyStableCoins) return false
    const s = debouncedQuery.toLowerCase().trim()
    return native && native.symbol?.toLowerCase?.()?.indexOf(s) !== -1
  }, [debouncedQuery, native, tokensToShow, mode, showOnlyStableCoins])

  const filteredTokens: Token[] = useMemo(() => {
    const filterToken = createFilterToken(debouncedQuery, (address) => isAddress(address))
    return Object.values(tokensToShow || allTokens).filter(filterToken)
  }, [tokensToShow, allTokens, debouncedQuery])

  const queryTokens = useSortedTokensByQuery(filteredTokens, debouncedQuery)
  const filteredQueryTokens = useMemo(() => {
    return queryTokens
  }, [queryTokens])

  const tokenComparator = useTokenComparator(invertSearchOrder)

  const stablePairs = useStableSwapPairs()

  const stableCoinAddresses = useMemo(() => {
    if (!showOnlyStableCoins) return null

    const addresses = new Set<string>()
    stablePairs.forEach((pair) => {
      if ('address' in pair.token0) addresses.add(pair.token0.address.toLowerCase())
      if ('address' in pair.token1) addresses.add(pair.token1.address.toLowerCase())
    })
    return addresses
  }, [stablePairs, showOnlyStableCoins])

  const filteredSortedTokens: Token[] = useMemo(() => {
    let tokens = [...filteredQueryTokens].sort(tokenComparator)

    if (showOnlyStableCoins && stableCoinAddresses) {
      tokens = tokens.filter((token) =>
        stableCoinAddresses.has(token.address.toLowerCase())
      )
    }

    return tokens
  }, [filteredQueryTokens, tokenComparator, showOnlyStableCoins, stableCoinAddresses])

  const handleCurrencySelect = useCallback(
    (currency: Currency) => {
      onCurrencySelect(currency)
      if (audioPlay) {
        getSwapSound().play()
      }
    },
    [audioPlay, onCurrencySelect],
  )

  const handleInput = useCallback((event) => {
    const input = event.target.value
    const checksummedInput = safeGetAddress(input)
    setSearchQuery(checksummedInput || input)
    fixedList.current?.scrollTo(0)
  }, [])

  const handleEnter = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const s = debouncedQuery.toLowerCase().trim()
        if (s === native.symbol.toLowerCase().trim()) {
          handleCurrencySelect(native)
        } else if (filteredSortedTokens.length > 0) {
          if (
            filteredSortedTokens[0].symbol?.toLowerCase() === debouncedQuery.trim().toLowerCase() ||
            filteredSortedTokens.length === 1
          ) {
            handleCurrencySelect(filteredSortedTokens[0])
          }
        }
      }
    },
    [debouncedQuery, filteredSortedTokens, handleCurrencySelect, native],
  )

  // if no results on main list, show option to expand into inactive
  const filteredInactiveTokens = useSearchInactiveTokenLists(debouncedQuery)

  const hasFilteredInactiveTokens = Boolean(filteredInactiveTokens?.length)

  const getCurrencyListRows = useCallback(() => {
    if (searchToken && !searchTokenIsAdded && !hasFilteredInactiveTokens) {
      return (
        <Column style={{ padding: '20px 0', height: '100%' }}>
          <ImportRow
            onCurrencySelect={handleCurrencySelect}
            token={searchToken}
            showImportView={showImportView}
            setImportToken={setImportToken}
          />
        </Column>
      )
    }

    return Boolean(filteredSortedTokens?.length) || hasFilteredInactiveTokens || mode === 'onramp-output' ? (
      <Box mx="-24px">
        <CurrencyList
          height={isMobile ? (showCommonBases ? height || 250 : 350) : 390}
          showNative={showNative}
          currencies={filteredSortedTokens}
          inactiveCurrencies={mode === 'onramp-input' ? [] : filteredInactiveTokens}
          breakIndex={
            Boolean(filteredInactiveTokens?.length) && filteredSortedTokens ? filteredSortedTokens.length : undefined
          }
          onCurrencySelect={handleCurrencySelect}
          otherCurrency={otherSelectedCurrency}
          selectedCurrency={selectedCurrency}
          fixedListRef={fixedList}
          showImportView={showImportView}
          setImportToken={setImportToken}
          mode={mode as string}
        />
      </Box>
    ) : (
      <Column style={{ padding: '20px', height: '100%' }}>
        <Text color="textSubtle" textAlign="center" mb="20px">
          {t('No results found.')}
        </Text>
      </Column>
    )
  }, [
    filteredInactiveTokens,
    filteredSortedTokens,
    handleCurrencySelect,
    hasFilteredInactiveTokens,
    otherSelectedCurrency,
    searchToken,
    searchTokenIsAdded,
    selectedCurrency,
    setImportToken,
    showNative,
    showImportView,
    t,
    showCommonBases,
    isMobile,
    height,
    mode,
  ])

  return (
    <>
      <AutoColumn gap="16px">
        {showSearchInput && (
          <Row>
            <SearchBox>
              <SearchIcon width={24} />
              <input
                placeholder={t(onRampFlow ? 'Search name' : 'Search name or paste address')}
                value={searchQuery}
                onChange={handleInput}
                onKeyDown={handleEnter}
                ref={inputRef}
              />
              {
                searchQuery?.length > 0 &&
                <CloseIcon width={24} onClick={() => {
                  setSearchQuery('')
                  inputRef?.current.focus()
                }} />
              }
            </SearchBox>
            {/* <ESearchBox
              style={{ backgroundColor: '#3E3A46' }}
              id="token-search-input"
              placeholder={t(onRampFlow ? 'Search name' : 'Search name or paste address')}
              scale="lg"
              autoComplete="off"
              value={searchQuery}
              onChange={handleInput}
              onKeyDown={handleEnter}
            /> */}
          </Row>
        )}
        {showCommonBases && !showOnlyStableCoins && (
          <CommonBases
            chainId={chainId}
            onSelect={handleCurrencySelect}
            selectedCurrency={selectedCurrency}
            commonBasesType={commonBasesType}
          />
        )}
      </AutoColumn>

      <AutoRow mt="24px">
        {/* <Text fontSize="18px">Token List</Text> */}
      </AutoRow>

      {getCurrencyListRows()}
    </>
  )
}

export default CurrencySearch
